// lib/quantityRules.ts
// How a product's quantity box counts. Pure, so it can be unit-tested.
//
// Default (every product whose feed doesn't say otherwise): start at 1, step 1,
// whole numbers — exactly the stepper's behaviour before 2026-10-09.
//
// bazaar's demo export added (see purchaseFields in skroutzXml.ts):
//   sold_by_weight + min_quantity → kg, starting at the minimum and stepping BY
//     the minimum (their own site: «το βήμα της ποσότητας είναι ίσο με το
//     minimum») — 0,2 → 0,4 → 0,6 kg
//   min_quantity on a piece product → starts at that number, steps by 1

export interface QuantityRule {
  min: number;
  step: number;
  /** Decimal places shown and sent; 0 = whole numbers. */
  decimals: number;
  unit?: 'kg';
}

export interface QuantityFields {
  sold_by_weight?: boolean;
  min_quantity?: number;
}

const DEFAULT_RULE: QuantityRule = { min: 1, step: 1, decimals: 0 };

/** Round away float noise (0.2 + 0.4 = 0.6000000000000001). */
function clean(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function decimalsOf(n: number): number {
  const s = String(clean(n));
  const dot = s.indexOf('.');
  return dot === -1 ? 0 : Math.min(3, s.length - dot - 1);
}

export function quantityRule(p: QuantityFields): QuantityRule {
  const min = typeof p.min_quantity === 'number' && p.min_quantity > 0 ? p.min_quantity : 1;
  if (p.sold_by_weight) return { min, step: min, decimals: decimalsOf(min), unit: 'kg' };
  if (min === 1) return DEFAULT_RULE;
  // A piece product with a minimum order (bazaar has 2 at «2»): whole numbers.
  return { min: Math.ceil(min), step: 1, decimals: 0 };
}

export function stepUp(q: number, r: QuantityRule): number {
  return clean(q + r.step);
}

export function stepDown(q: number, r: QuantityRule): number {
  return Math.max(r.min, clean(q - r.step));
}

/** A typed number → the nearest valid quantity (a multiple of the step, ≥ min). */
export function snapQuantity(typed: number, r: QuantityRule): number {
  if (!Number.isFinite(typed) || typed <= 0) return r.min;
  const snapped = r.decimals > 0 ? clean(Math.round(typed / r.step) * r.step) : Math.round(typed);
  return Math.max(r.min, snapped);
}

/** What the box shows: Greek decimal comma, no trailing zeros («0,2», «1,5», «2»). */
export function formatQuantity(q: number, r: QuantityRule): string {
  return r.decimals > 0 ? String(clean(q)).replace('.', ',') : String(q);
}
